import React from "react";
import { Spin } from "antd";
import CgIcon from "./CgIcon";
import styles from "../Orders/saleEntry.module.css";

/*
 * The ledger that opens under a line, as the approved mockup draws it
 * (`.seInHist` / `.phHistT` in curengo-pharmacy-price-history.html).
 *
 * The chrome only: the violet bar tying the panel to its line, the heading
 * with the subject and the "dearer or cheaper than last time" pill, the fold
 * toggle, and the strip itself. What goes in the columns is the caller's -
 * a sale is read off bills and a purchase off entries, and the two name
 * different things - so the columns and the cells are passed in and this
 * holds the shape they are printed in.
 *
 * `columns` is `[{ key, label, align, width }]`; `align` is "ctr" or "end",
 * and `width` is that column's grid track. `rows` is
 * `[{ key, cells: { <column key>: node }, dim: [keys], strong: [keys] }]`,
 * newest first - the first row is the one the panel picks out.
 */

const EM_DASH = "—";

/* Both ledgers are headed the same - what a medicine has been going for is
   one question whichever side of the counter is asking it. Overridable, but
   nothing overrides it today. */
const DEFAULT_TITLE = "Price History";

export const HistoryNote = ({ title = DEFAULT_TITLE, children }) => (
  <div className={styles.seInHist}>
    <div className={styles.seInHead}>
      <CgIcon name="clock" size={14} stroke={2.2} />
      <b>{title}</b>
    </div>
    <div className={styles.seInNote}>{children}</div>
  </div>
);

export const HistoryLoading = ({ title = DEFAULT_TITLE }) => (
  <div className={styles.seInHist}>
    <div className={styles.seInHead}>
      <CgIcon name="clock" size={14} stroke={2.2} />
      <b>{title}</b>
    </div>
    <div style={{ padding: "4px 18px 14px" }}>
      <Spin size="small" />
    </div>
  </div>
);

/*
 * How the rate being typed now compares with the last one paid.
 *
 * The whole reason the panel is open: not what the ledger says, but whether
 * today's figure is out of line with it. Returned as a tone and a sentence so
 * the caller can word it for its own side of the counter.
 */
export const rateDelta = (currentRate, lastRate, { higher, lower, same }) => {
  const typed = Number(currentRate);
  if (!Number.isFinite(typed) || typed <= 0) {
    return { tone: "flat", text: "No rate yet" };
  }
  const delta = Math.round((typed - lastRate) * 100) / 100;
  if (delta > 0.009) return { tone: "up", text: higher(delta) };
  if (delta < -0.009) return { tone: "down", text: lower(-delta) };
  return { tone: "flat", text: same };
};

const HistoryStrip = ({ title = DEFAULT_TITLE, subject, delta, toggle, columns, rows }) => {
  const template = columns.map((column) => column.width).join(" ");

  return (
    <div className={styles.seInHist}>
      <div className={styles.seInHead}>
        <CgIcon name="clock" size={14} stroke={2.2} />
        <b>{title}</b>
        {subject ? <span className={styles.seInWho}>{subject}</span> : null}
        {delta ? (
          <span className={`${styles.seDelta} ${styles[delta.tone]}`}>{delta.text}</span>
        ) : null}
        <div style={{ flex: 1 }} />
        {toggle ? (
          <button
            type="button"
            className={`${styles.seHistTog} ${toggle.open ? styles.on : ""}`}
            onClick={toggle.onToggle}
          >
            {toggle.label}
            <CgIcon name="chev" size={14} stroke={2.4} />
          </button>
        ) : null}
      </div>

      <div className={styles.seInScroll}>
        <div
          className={styles.phHistT}
          /* Each side brings its own columns; the mockup's own twelve are the
             fallback, so the sale strip is untouched by this being shared. */
          style={{ "--ph-cols": template }}
        >
          <div className={`${styles.prow} ${styles.phCols} ${styles.phdr}`}>
            {columns.map((column) => (
              <div
                key={column.key}
                className={column.align ? styles[column.align] : undefined}
              >
                {column.label}
              </div>
            ))}
          </div>

          <div
            className={[
              styles.seHist,
              rows.length > 5 ? styles.cap : null,
              toggle?.open ? styles.in : null,
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {rows.map((row, index) => (
              <div
                key={row.key}
                className={[
                  styles.prow,
                  styles.phCols,
                  styles.seHistRow,
                  index === 0 ? styles.last : null,
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{ "--i": index }}
              >
                {columns.map((column) => {
                  const value = row.cells[column.key];
                  return (
                    <div
                      key={column.key}
                      className={[
                        column.align ? styles[column.align] : null,
                        column.tnum === false ? null : styles.tnum,
                        row.dim?.includes(column.key) ? styles.dim : null,
                        row.strong?.includes(column.key) ? styles.bno : null,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {value === null || value === undefined || value === "" ? EM_DASH : value}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryStrip;
